import classNames from 'classnames'
import type { ChangeEvent, ComponentProps } from 'react'
import React, { forwardRef, useEffect, useId, useRef } from 'react'
import { BreakoutTooltip } from './BreakoutTooltip'

interface SwitchProps extends ComponentProps<'input'> {
  label?: string | React.ReactNode
  labelPosition?: 'left' | 'right'
  labelClassName?: string
  className?: string
  disabled?: boolean
  initialValue?: boolean
  testId?: string
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void
  containerProps?: React.HTMLAttributes<HTMLDivElement>
  labelProps?: React.HTMLAttributes<HTMLLabelElement>
  circleProps?: React.HTMLAttributes<HTMLLabelElement>
  inputRef?: React.Ref<HTMLInputElement>
  accent?: boolean
  tooltip?: string
  /**
   * Set when a <label htmlFor> outside this component already points at the
   * switch. The knob is a <label> for the same id, so leaving it be would give
   * the checkbox two labels sharing one `for` value. This renders the knob as a
   * plain <span> that toggles on click instead, leaving the outside label
   * as the only one.
   */
  hasExternalLabel?: boolean
}

export const BreakoutSwitch = forwardRef<HTMLInputElement, SwitchProps>(
  function BreakoutSwitch(
    {
      label,
      labelPosition = 'right',
      disabled,
      initialValue,
      onChange,
      testId,
      className,
      containerProps,
      circleProps,
      labelProps,
      accent = false,
      tooltip,
      labelClassName,
      hasExternalLabel = false,
      ...rest
    },
    ref
  ) {
    const switchId = useId()
    const internalInputRef = useRef<HTMLInputElement | null>(null)

    disabled = disabled ?? false

    const labelComponent = (
      <label
        {...labelProps}
        htmlFor={rest.id || switchId}
        className={classNames(
          'px-4 text-body-medium text-on-surface-var select-none',
          {
            'cursor-pointer': !disabled,
            'cursor-not-allowed': disabled,
          },
          labelClassName,
          labelProps?.className
        )}
      >
        {label}
      </label>
    )

    useEffect(() => {
      if (internalInputRef.current) {
        if (initialValue !== undefined) {
          internalInputRef.current.checked = initialValue
        }
      }
    }, [internalInputRef, initialValue])

    const handleToggle = () => {
      if (disabled) return
      internalInputRef.current?.click()
    }

    // Without a `for` the knob labels nothing, so render it as the plain
    // decoration it is rather than an empty <label>.
    const Knob = hasExternalLabel ? 'span' : 'label'

    return (
      // Clicks on the gap around the label and switch (callers often stretch
      // this row with w-full justify-between) toggle as a mouse shortcut. The
      // input and its labels handle their own clicks, so only react to clicks
      // that land on this wrapper itself. Keyboard users have the input.
      <div
        ref={ref}
        role="presentation"
        onClick={(e) => {
          if (e.target === e.currentTarget) handleToggle()
        }}
        className={classNames(
          'inline-flex items-center',
          {
            'opacity-50': disabled,
            'cursor-pointer': !disabled,
            'cursor-not-allowed': disabled,
          },
          className
        )}
      >
        {label && labelPosition === 'left' && labelComponent}
        <BreakoutTooltip content={tooltip || ''} enabled={!!tooltip}>
          <div
            {...containerProps}
            className={classNames(
              'relative inline-block h-4 w-8 rounded-full',
              {
                'cursor-pointer': !disabled,
                'cursor-not-allowed': disabled,
              },
              containerProps?.className
            )}
          >
            <input
              // oxlint-disable-next-line jsx-a11y/role-has-required-aria-props -- a checkbox input exposes its checked state itself, so aria-checked is not needed
              role="switch"
              {...rest}
              ref={(ref) => {
                internalInputRef.current = ref
              }}
              type="checkbox"
              disabled={disabled}
              id={rest.id || switchId}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleToggle()
                }
              }}
              className={classNames(
                "before:content[''] focus-ring-double peer absolute h-4 w-8 appearance-none rounded-full bg-surface-dim transition-colors duration-300 peer-checked:border-gray-900 before:absolute before:-top-1 before:left-2 before:block before:h-6 before:w-6 checked:before:left-0",
                {
                  'peer-checked:before:bg-core-primary checked:bg-core-primary':
                    !accent,
                  'peer-checked:before:bg-fixed-accent-color checked:bg-fixed-accent-color':
                    accent,
                  'cursor-pointer': !disabled,
                  'cursor-not-allowed': disabled,
                  'opacity-50': disabled,
                }
              )}
              onChange={onChange}
            />
            <Knob
              data-testid={testId ? testId : undefined}
              {...circleProps}
              htmlFor={hasExternalLabel ? undefined : rest.id || switchId}
              onClick={hasExternalLabel ? handleToggle : undefined}
              className={classNames(
                "border-blue-gray-100 before:content[''] before:bg-blue-gray-500 before:pointer-events absolute top-2/4 -left-1 h-5 w-5 -translate-y-2/4 rounded-full border bg-core-tertiary shadow-md transition-all duration-300 peer-checked:translate-x-full before:absolute before:top-2/4 before:left-2/4 before:block before:h-10 before:w-10 before:-translate-x-2/4 before:-translate-y-2/4 before:rounded-full before:opacity-0 before:transition-opacity",
                {
                  'peer-checked:border-gray-900 peer-checked:before:bg-core-primary':
                    !accent,
                  'peer-checked:border-fixed-accent-color peer-checked:before:bg-fixed-accent-color':
                    accent,
                  'cursor-pointer hover:before:opacity-10': !disabled,
                  'cursor-not-allowed': disabled,
                },
                circleProps?.className
              )}
            ></Knob>
          </div>
        </BreakoutTooltip>
        {label && labelPosition === 'right' && labelComponent}
      </div>
    )
  }
)
